import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import throttle from 'lodash/throttle'
import { detect } from 'detect-browser'
import { Portal } from 'react-portal'

import {
  colors,
  mediaQueries,
  Text,
  ThemeProvider as PricelineThemeProvider
} from 'pcln-design-system'
import { Close } from 'pcln-icons'

import { OverlayManager } from './OverlayManager'

const PADDING_PX = 12
const HEADER_HEIGHT = 44

const headerThemeMap = {
  default: {
    color: 'text',
    background: 'white'
  },
  pcln: {
    color: 'white',
    background: 'blue'
  }
}

function getDialogCss(props) {
  if (props.fullScreen) {
    return `
      height: 100%;
      width: 100%;
    `
  }
  return `${
    props.mobileBreakpoint
      ? `@media screen and (min-width: ${props.mobileBreakpoint}px)`
      : mediaQueries.md
  } {
    max-height: ${props.maxDialogHeightPct}%;
    width: auto;
  }`
}

const OverlayBg = styled.div`
  -webkit-overflow-scrolling: touch;
  align-items: center;
  background: rgba(51, 51, 51, 0.9);
  bottom: 0;
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 1000;
`

const Dialog = styled.div`
  background-color: ${props =>
    props.backgroundColor === 'transparent'
      ? 'transparent'
      : colors[props.backgroundColor]};
  height: auto;
  max-width: ${props => (props.fullScreenOnMobile ? 'unset' : '90%')};
  overflow: hidden;
  position: relative;
  width: ${props =>
    props.fullScreenOnMobile || props.fullScreen ? '100%' : 'auto'};

  ${props => getDialogCss(props)}
`
/* eslint-disable */
const Content = styled.div`
  background-color: ${props =>
    props.backgroundColor === 'transparent'
      ? 'transparent'
      : colors[props.backgroundColor]};
  padding: 0 ${props => (props.padContent ? PADDING_PX : 0)}px;
  overflow: ${props => (props.iOSOverflowVisible ? 'scroll' : 'hidden')};

  ${props =>
    !props.iOSDisableContentScroll &&
    `
    @supports (-webkit-overflow-scrolling: touch) {
      /* CSS specific to iOS devices */
      overflow: ${props.iOSOverflowVisible ? 'visible' : 'hidden'};
    }
  `}
`
/* eslint-enable */

const CloseBtn = styled.div`
  padding: ${PADDING_PX}px;
  z-index: 105;

  :hover {
    cursor: pointer;
  }
`

const Header = styled.div`
  align-items: center;
  background-color: ${props =>
    colors[headerThemeMap[props.headerTheme].background]};
  color: ${props => colors[headerThemeMap[props.headerTheme].color]};
  display: flex;
  flex-flow: row nowrap;
  height: ${HEADER_HEIGHT}px;
  justify-content: space-between;
`

const Footer = styled.div`
  align-items: center;
  bottom: 0;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  left: 0;
  width: 100%;
`

class Overlay extends React.Component {
  constructor(props) {
    super(props)

    this.overlayManager = new OverlayManager()
    this.throttledHandleResize = throttle(this.handleResize, 200)

    this.state = {
      pageXOffset: null,
      pageYOffset: null,
      dialogId: 'dialog',
      contentId: 'overlay-content',
      footerId: 'footer-content',
      overlayId: 'overlay-bg'
    }
  }

  /**
   * Returns the first DOM element above el that has the given class. If no
   * matching element found, returns null.
   *
   * Source: https://stackoverflow.com/a/22119674
   *
   * @param {Element} el
   * @param {String} cls
   */
  findAncestor = (el, cls) => {
    // eslint-disable-next-line no-param-reassign
    while ((el = el.parentElement) && !el.classList.contains(cls));
    return el
  }

  componentDidMount() {
    this.browser = detect()
    const overlayId = this.overlayManager.add(this)

    if (typeof window === 'undefined') {
      return
    }

    if (!this.props.fullScreen) {
      window.addEventListener('resize', this.throttledHandleResize)
    }

    this.setState(
      {
        pageYOffset: window.pageYOffset,
        pageXOffset: window.pageXOffset,
        dialogId: `dialog-${overlayId}`,
        contentId: `overlay-content-${overlayId}`,
        footerId: `footer-content-${overlayId}`,
        overlayId: `overlay-bg-${overlayId}`
      },
      () => {
        // Set CSS rules on document and body to handle scrolling when overlay is open. We only need to do this
        // for the first overlay in the event that overlays are nested.
        if (this.overlayManager.getOpenModalCount() <= 1) {
          this.setBodyCss()
        }

        // Handle initial resize of dialog and content. This relies on element IDs set on state.
        this.handleResize()
      }
    )
  }

  componentWillUnmount() {
    window.scrollTo(this.state.pageXOffset, this.state.pageYOffset)
    window.removeEventListener('resize', this.throttledHandleResize)

    // Only clean up document styles if this is the last open modal
    if (this.overlayManager.remove()) {
      this.cleanupDocumentStyles()
    }
  }

  componentDidUpdate() {
    const content = document.getElementById(this.state.contentId)

    if (!content) return

    // This is a hack to fix scrolling on iOS devices. Wait for the overlay to finish rendering, then
    // remove and add overflow CSS rules back to the content. This likely has to do with interactions between
    // z-index and touch scrolling.
    //
    // Sources:
    //  - https://github.com/scottjehl/Device-Bugs/issues/14#issuecomment-8075213
    //  - https://stackoverflow.com/a/28748160
    window.requestAnimationFrame(() => {
      content.style.overflowY = ''
      content.style.webkitOverflowScrolling = ''
      setTimeout(() => {
        content.style.overflowY = this.props.forceiOSOverflowVisible
          ? 'scroll'
          : 'hidden'
        content.style.webkitOverflowScrolling = 'touch'
      })
    })
  }

  cleanupDocumentStyles = () => {
    ;['overflow', 'position', 'width', 'top'].map(prop => {
      document.body.style[prop] = ''
      document.documentElement.style[prop] = ''
    })

    // Restore original scroll position
    window.scrollTo(this.state.pageXOffset, this.state.pageYOffset)
  }

  setBodyCss = () => {
    let position = 'fixed'
    let top = this.state.pageYOffset > 0 ? `-${this.state.pageYOffset}px` : ''

    // Don't set position or top in IE or Safari
    if (this.browser && this.browser.name) {
      switch (this.browser.name) {
        case 'safari':
          position = ''
          top = ''
          break
        case 'ios':
          // still need position fixed to prevent body from scrolling behind overlay
          top = ''
          break
        default:
          break
      }
    }

    Object.assign(document.body.style, {
      overflow: 'hidden',
      width: '100%',
      position,
      top
    })
  }

  close = () => {
    const { onClose } = this.props

    // TODO fix dismissOnClose

    if (typeof onClose === 'function') {
      onClose()
    }
  }

  dismiss = e => {
    // Don't do anything if user clicked within the dialog
    if (this.findAncestor(e.target, 'dialog')) {
      return
    }

    // TODO fix dismiss (findAncestor is broken maybe)

    this.close()
  }

  /**
   * Dynamically set height property on dialog based on height of content relative to the height of the screen
   */
  handleResize = (resize = {}) => {
    const { height } = resize

    // Do nothing on mobile and when rendering on server
    if (typeof window === 'undefined') {
      return
    }

    const { dialogId, contentId, footerId, overlayId } = this.state

    const dialog = document.getElementById(dialogId)
    const content = document.getElementById(contentId)
    const footer = document.getElementById(footerId)
    const overlay = document.getElementById(overlayId)

    // Prevent null reference caused by extra renders
    if (!(dialog && content && footer && overlay)) {
      return
    }

    // Handle fullscreen
    if (this.props.fullScreen) {
      dialog.style.height = '100%'
      content.style.height = `calc(100% - ${HEADER_HEIGHT +
        footer.clientHeight}px)`
      return
    }

    // Handle small screens
    if (
      this.props.fullScreenOnMobile &&
      document.documentElement.clientWidth < this.props.mobileBreakpoint
    ) {
      dialog.style.height = '100%'
      content.style.height = `calc(100% - ${HEADER_HEIGHT +
        footer.clientHeight}px)`
      return
    }

    // Compute point at which to transition from height auto to unset
    const breakpointHeight = Math.floor(
      overlay.clientHeight * this.props.maxDialogHeightPct * 0.01 -
        (HEADER_HEIGHT + footer.clientHeight)
    )

    // We know we're not on a mobile screen, so reset content height before deciding
    // whether or not to give content a maxHeight
    content.style.height = ''

    // Add or remove height property depending on height of content
    if (
      !this.props.fullHeight &&
      (height || content.clientHeight) < breakpointHeight
    ) {
      content.style.height = 'auto'
      dialog.style.height = 'auto'
    } else {
      dialog.style.height = '100%'
      content.style.height = '100%'
      content.style.maxHeight = `calc(100% - ${HEADER_HEIGHT +
        footer.clientHeight}px)`
    }
  }

  // Store reference to noop for use in render
  noop = () => {}

  render() {
    const {
      padContent,
      showHeaderTitle,
      hideHeader,
      header,
      content,
      footer,
      fullScreenOnMobile,
      mobileBreakpoint,
      maxDialogHeightPct,
      headerTheme,
      fullScreen,
      hideFooter
    } = this.props
    const { dialogId, contentId, footerId, overlayId } = this.state

    return (
      <Portal>
        {/* TODO: Refactor so that theme provider is not needed
          and it will be passed through from the parent container */}
        <PricelineThemeProvider>
          <OverlayBg id={overlayId} onClick={this.dismiss}>
            <Dialog
              id={dialogId}
              className={'dialog'}
              fullScreenOnMobile={fullScreenOnMobile}
              mobileBreakpoint={mobileBreakpoint}
              maxDialogHeightPct={maxDialogHeightPct}
              fullScreen={fullScreen}
              backgroundColor={this.props.backgroundColor}
            >
              {/* header and close button */}
              {!hideHeader && (
                <Header headerTheme={headerTheme}>
                  <Text fontSize={3} m={PADDING_PX}>
                    {showHeaderTitle ? header : ''}
                  </Text>
                  <CloseBtn
                    onClick={this.close}
                    data-autobot-element='modal-close'
                  >
                    <Close size={16} />
                  </CloseBtn>
                </Header>
              )}

              {/* dialog body */}
              <Content
                id={contentId}
                padContent={padContent}
                backgroundColor={this.props.backgroundColor}
                iOSOverflowVisible={this.props.forceiOSOverflowVisible}
                iOSDisableContentScroll={this.props.iOSDisableContentScroll}
              >
                {content}
              </Content>
              {!hideFooter && <Footer id={footerId}>{footer}</Footer>}
            </Dialog>
          </OverlayBg>
        </PricelineThemeProvider>
      </Portal>
    )
  }
}

Overlay.defaultProps = {
  showHeader: true,
  padContent: true,
  headerTheme: 'default',
  dismissOnClose: true,
  fullScreenOnMobile: true,
  maxDialogHeightPct: 80,
  mobileBreakpoint: 640,
  fullScreen: false,
  forceiOSOverflowVisible: false,
  iOSDisableContentScroll: false,
  hideHeader: false,
  backgroundColor: 'white',
  hideFooter: false
}

Overlay.propTypes = {
  // Should title text be displayed
  showHeaderTitle: PropTypes.bool,

  // Element to insert as title
  header: PropTypes.node,

  // Header color overide
  headerTheme: PropTypes.string,

  // Hide the entire header INCLUDING CLOSE BUTTON
  hideHeader: PropTypes.bool,

  // Element to insert in content area
  content: PropTypes.node,

  // Element to insert in footer area
  footer: PropTypes.node,

  // Called when modal is closed or dismissed
  onClose: PropTypes.func,

  // Add padding around dialog content
  padContent: PropTypes.bool,

  // Overlay will automatically display:none on onClose
  dismissOnClose: PropTypes.bool,

  // Fill entire screen on screens smaller than props.mobileBreakpoint
  fullScreenOnMobile: PropTypes.bool,

  // Modal will always stretch to fill 100% of screen height
  fullHeight: PropTypes.bool,

  // Fill entire screen at all breakpoints
  fullScreen: PropTypes.bool,

  // Max height (%) of overlay
  maxDialogHeightPct: PropTypes.number,

  // Screens below this width are considered mobile
  mobileBreakpoint: PropTypes.number,

  // Background color for content area, defaults to white
  backgroundColor: PropTypes.string,

  // Disable content scrolling. Useful when you want the content to handle its own scrolling.
  iOSDisableContentScroll: PropTypes.bool,

  // For iOS devices ONLY, force content's CSS overflow for use 'visible' to fix a scrolling bug
  // NOTE: This is a hack until we're fully on React
  forceiOSOverflowVisible: PropTypes.bool,

  // Hide entire footer
  hideFooter: PropTypes.bool
}

export { Overlay }
